import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, CardHeader, EmptyState, Pagination, Skeleton } from "@/hq/HQPrimitives";

const TYPES = [
  "ORANGE_SCAN","WEBSITE_TRANSFORMATION","ORANGE_OPTIMISE",
  "ORANGECARE","CRM_DISCOVERY","LANDING_PAGE",
  "SEASONAL_OFFER","PROMOTION","REFERRAL","CUSTOM",
];

const STATE_STYLE_LIST = {
  DRAFT: "bg-[#F2F2F2] text-[#4A4A4A] border-[#EAEAEA]",
  READY: "bg-[#F2F2F2] text-[#4A4A4A] border-[#EAEAEA]",
  SCHEDULED: "bg-[#F0F7FF] text-[#2E7CF6] border-[#D6E7FF]",
  SENDING: "bg-[#F0F7FF] text-[#2E7CF6] border-[#D6E7FF]",
  PARTIALLY_SENT: "bg-[#FFF7EE] text-[#8A6D00] border-[#FCE1C7]",
  SENT: "bg-[#EBFBF2] text-[#0FA958] border-[#B7EBCF]",
  DELIVERY_BLOCKED: "bg-[#FDECE7] text-[#C1350B] border-[#F6C0AF]",
  PAUSED: "bg-[#F2F2F2] text-[#4A4A4A] border-[#EAEAEA]",
  CANCELLED: "bg-[#F2F2F2] text-[#4A4A4A] border-[#EAEAEA]",
  FAILED: "bg-[#FDECE7] text-[#C1350B] border-[#F6C0AF]",
};
const STATE_TEXT_LIST = {
  DELIVERY_BLOCKED: "Delivery blocked",
  PARTIALLY_SENT: "Partially sent",
};

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "Orange Scan — Q1 outreach",
    campaign_type: "ORANGE_SCAN",
    subject: "A quick look at your website",
    cta_label: "See the details",
    cta_url: "",
  });
  const [saving, setSaving] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await hqApi.post("/sales/campaigns", form);
      toast.success("Campaign created.");
      onCreated?.(data.campaign);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="campaign-create-modal">
      <form onSubmit={submit} className="w-full max-w-lg bg-white rounded-2xl border border-[#EAEAEA] p-6 space-y-3">
        <h3 className="font-black text-lg">Create campaign</h3>
        <label className="block">
          <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-input-name" className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-[13px]" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Type</span>
          <select value={form.campaign_type} onChange={(e) => setForm({ ...form, campaign_type: e.target.value })} data-testid="campaign-input-type" className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-[13px]">
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Subject</span>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="campaign-input-subject" className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-[13px]" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">CTA label</span>
          <input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} data-testid="campaign-input-cta-label" className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-[13px]" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">CTA URL (optional)</span>
          <input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} data-testid="campaign-input-cta-url" className="mt-1 w-full px-3 py-2 border border-[#EAEAEA] rounded-lg text-[13px]" placeholder="https://orangebysite.com/optimise" />
        </label>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} data-testid="campaign-create-cancel" className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[12px] font-bold tracking-[0.12em] uppercase">Cancel</button>
          <button type="submit" disabled={saving} data-testid="campaign-create-submit" className="px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[12px] font-black tracking-[0.12em] uppercase disabled:opacity-50">
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function HQSalesCampaigns() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [ct, setCt] = useState("");
  const [st, setSt] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await hqApi.get("/sales/campaigns", { params: { page, page_size: 25, campaign_type: ct || undefined, state: st || undefined } });
      setData(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, ct, st]);

  return (
    <div className="space-y-4">
      <Card>
        <div className="px-6 py-4 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Sales Engine</div>
            <h2 className="mt-1 font-black text-lg">Campaigns</h2>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="campaigns-create-button" className="px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[12px] font-black tracking-[0.12em] uppercase">Create campaign</button>
        </div>
        <div className="px-6 pb-4 flex gap-2">
          <select value={ct} onChange={(e) => { setCt(e.target.value); setPage(1); }} data-testid="campaigns-filter-type" className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px]">
            <option value="">All types</option>{TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select value={st} onChange={(e) => { setSt(e.target.value); setPage(1); }} data-testid="campaigns-filter-state" className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px]">
            <option value="">All states</option>
            {["DRAFT","READY","SCHEDULED","SENDING","PARTIALLY_SENT","SENT","DELIVERY_BLOCKED","PAUSED","CANCELLED","FAILED"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="px-6 pb-6">
          {loading ? <Skeleton h={80} /> : !data.items?.length ? (
            <EmptyState title="No campaigns yet" hint="Create the first Orange Scan campaign." testId="campaigns-empty" />
          ) : (
            <table className="w-full text-[13px]">
              <thead><tr className="text-left text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A]">
                <th className="py-2">Name</th><th>Type</th><th>State</th><th>Recipients</th><th>Sent · Opens · Clicks</th><th></th>
              </tr></thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-t border-[#EAEAEA]" data-testid={`campaign-row-${c.id}`}>
                    <td className="py-3 font-semibold">{c.name}</td>
                    <td className="py-3 text-[12px]">{c.campaign_type}</td>
                    <td className="py-3"><span className={`px-2 py-1 rounded-full border text-[10px] font-black tracking-[0.16em] ${STATE_STYLE_LIST[c.state] || STATE_STYLE_LIST.DRAFT}`} data-testid={`campaign-list-state-${c.id}`}>{STATE_TEXT_LIST[c.state] || c.state}</span></td>
                    <td className="py-3">{c.recipient_count || 0}</td>
                    <td className="py-3 text-[12px] tabular-nums" title="Sent · Opens detected (noisy) · Clicks">{c.sent_count || 0} · {c.opens || 0} · {c.clicks || 0}</td>
                    <td className="py-3"><Link to={`/hq/sales/campaigns/${c.id}`} className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#FF6A00]" data-testid={`campaign-open-${c.id}`}>Open →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="campaigns-page" />
        </div>
      </Card>
      {showAdd && <CreateModal onClose={() => setShowAdd(false)} onCreated={() => load()} />}
    </div>
  );
}
